import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { OnboardingCheck } from '@/components/auth/OnboardingCheck';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { OrganizationProvider } from '@/hooks/useOrganization';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AccountDetail } from '@/pages/AccountDetail';
import { Accounts } from '@/pages/Accounts';
import { BalanceSheet } from '@/pages/BalanceSheet';
import { BankAccounts } from '@/pages/BankAccounts';
import { Dashboard } from '@/pages/Dashboard';
import { EmailVerificationPage } from '@/pages/EmailVerificationPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { Inventory } from '@/pages/Inventory';
import { InventoryValuationPage } from '@/pages/InventoryValuation';
import { Journal } from '@/pages/Journal';
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { ProfitLoss } from '@/pages/ProfitLoss';
import { Reconciliation } from '@/pages/Reconciliation';
import { RegistrationPage } from '@/pages/RegistrationPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { TaxCodes } from '@/pages/TaxCodes';
import { TaxSummary } from '@/pages/TaxSummary';
import { Transactions } from '@/pages/Transactions';
import { TrialBalancePage } from '@/pages/TrialBalance';

function AppRoutes() {
  const { user, loading } = useAuth();

  console.log('[AppRoutes] Rendering, loading:', loading, 'user:', user?.email);

  if (loading) {
    console.log('[AppRoutes] Showing loading screen');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🌿</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  console.log('[AppRoutes] User state resolved, user:', user?.email, 'redirecting to:', user ? '/app' : '/');
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/app" replace /> : <RegistrationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/onboarding/organization" element={<OnboardingPage />} />
      
      {/* Protected routes - using Outlet pattern */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout>
                <Outlet />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/:id" element={<AccountDetail />} />
        <Route path="journal" element={<Journal />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="bank-accounts" element={<BankAccounts />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="tax-codes" element={<TaxCodes />} />
        <Route path="reports/trial-balance" element={<TrialBalancePage />} />
        <Route path="reports/balance-sheet" element={<BalanceSheet />} />
        <Route path="reports/profit-loss" element={<ProfitLoss />} />
        <Route path="reports/tax-summary" element={<TaxSummary />} />
        <Route path="reports/inventory-valuation" element={<InventoryValuationPage />} />
      </Route>

      {/* Legacy routes redirect to /app */}
      <Route path="/accounts" element={<Navigate to="/app/accounts" replace />} />
      <Route path="/accounts/:id" element={<Navigate to="/app/accounts/:id" replace />} />
      <Route path="/journal" element={<Navigate to="/app/journal" replace />} />
      <Route path="/transactions" element={<Navigate to="/app/transactions" replace />} />
      <Route path="/reconciliation" element={<Navigate to="/app/reconciliation" replace />} />
      <Route path="/bank-accounts" element={<Navigate to="/app/bank-accounts" replace />} />
      <Route path="/inventory" element={<Navigate to="/app/inventory" replace />} />
      <Route path="/tax-codes" element={<Navigate to="/app/tax-codes" replace />} />
      <Route path="/reports/*" element={<Navigate to="/app/reports/*" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  console.log('[App] Initializing app');
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          <AppRoutes />
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}